/**
 * Order Status Configuration - Simplified Workflow
 *
 * This file defines the 15-status simplified workflow for order processing.
 * See /DOCS/new-workflow.md for full documentation.
 *
 * WORKFLOW PHASES:
 * 1 - Review (Order Entry): REVIEW, ORDER_CREATED
 * 2 - Supplier Selection & Quoting: SUPPLIER_SELECTION, QUOTE_REQUESTED, QUOTE_RECEIVED, SET_MARGIN
 * 3 - Proforma & Negotiation: PROFORMA_GENERATED, PROFORMA_SENT, NEGOTIATION, ADJUST_RESEND, PROFORMA_RESENT
 * 4 - PO & Payment: PO_CREATED, LOG_PAYMENT
 * 5 - Production: PRODUCTION, PACKING_LIST_RECEIVED
 * 6 - Document Verification & Shipping: DOC_VERIFICATION, SHIPMENT
 * 7 - Delivery: DELIVERED
 *
 * Terminal States: ORDER_REJECTED, ORDER_CANCELLED
 *
 * Color meanings:
 *   - blue:   Action needed by user
 *   - purple: Decision/selection needed
 *   - amber:  Waiting for external party
 *   - teal:   Ready to proceed/send
 *   - green:  Completed/approved
 *   - urgent: Overdue/urgent (red)
 */

export type BadgeColor = 'blue' | 'purple' | 'amber' | 'teal' | 'green' | 'urgent';

/**
 * Simplified workflow status identifiers (15 statuses + 2 terminal states)
 */
export type OrderStatusId =
  // === PHASE 1: REVIEW (Order Entry) ===
  | 'ORDER_RECEIVED'
  | 'REVIEW' // Order under review
  | 'ORDER_CREATED' // Order created and ready for supplier selection

  // === PHASE 2: SUPPLIER SELECTION & QUOTING ===
  | 'SUPPLIER_SELECTION' // Selecting suppliers for quote requests
  | 'QUOTE_REQUESTED' // Quote requests sent to suppliers
  | 'QUOTE_RECEIVED' // Quotes received from suppliers
  | 'SET_MARGIN' // Setting profit margin on quote

  // === PHASE 3: PROFORMA & NEGOTIATION ===
  | 'PROFORMA_GENERATED' // Proforma invoice generated
  | 'PROFORMA_SENT' // Proforma invoice sent to customer
  | 'NEGOTIATION' // Negotiating terms with customer (optional)
  | 'ADJUST_RESEND' // Adjusting proforma for resend (optional)
  | 'PROFORMA_RESENT' // Updated proforma resent to customer (optional)

  // === PHASE 4: PO & PAYMENT ===
  | 'PO_CREATED' // Purchase order created with supplier
  | 'LOG_PAYMENT' // Payment received and logged (can occur multiple times)

  // === PHASE 5: PRODUCTION ===
  | 'PRODUCTION' // Order in production
  | 'PACKING_LIST_RECEIVED' // Packing list received from supplier

  // === PHASE 6: DOCUMENT VERIFICATION & SHIPPING ===
  | 'DOC_VERIFICATION' // Verifying shipping documents
  | 'SHIPMENT' // Order in transit

  // === PHASE 7: DELIVERY ===
  | 'DELIVERED' // Order delivered to customer (terminal state)

  // === TERMINAL STATES ===
  | 'ORDER_REJECTED' // Order rejected (terminal state)
  | 'ORDER_CANCELLED'; // Order cancelled (terminal state)

export interface StatusBadgeConfig {
  id: OrderStatusId;
  label: string;
  icon: string;
  color: BadgeColor;
  description: string;
  phase?: number; // Which phase this status belongs to (1-9, 0 for opportunity)
}

/**
 * Main status configuration for simplified workflow.
 * Maps status IDs to their display configuration.
 */
export const ORDER_STATUS_CONFIG: Record<OrderStatusId, StatusBadgeConfig> = {
  // === PHASE 1: REVIEW (Order Entry) ===
  ORDER_RECEIVED: {
    id: 'REVIEW',
    label: 'Review',
    icon: 'fas fa-eye',
    color: 'blue',
    description: 'Order under review',
    phase: 1,
  },
  REVIEW: {
    id: 'REVIEW',
    label: 'Review',
    icon: 'fas fa-eye',
    color: 'blue',
    description: 'Order under review',
    phase: 1,
  },
  ORDER_CREATED: {
    id: 'ORDER_CREATED',
    label: 'Order Created',
    icon: 'fas fa-check-circle',
    color: 'green',
    description: 'Order created and ready for supplier selection',
    phase: 1,
  },

  // === PHASE 2: SUPPLIER SELECTION & QUOTING ===
  SUPPLIER_SELECTION: {
    id: 'SUPPLIER_SELECTION',
    label: 'Select Suppliers',
    icon: 'fas fa-industry',
    color: 'purple',
    description: 'Selecting suppliers for quote requests',
    phase: 2,
  },
  QUOTE_REQUESTED: {
    id: 'QUOTE_REQUESTED',
    label: 'Quote Requested',
    icon: 'fas fa-paper-plane',
    color: 'teal',
    description: 'Quote requests sent to suppliers',
    phase: 2,
  },
  QUOTE_RECEIVED: {
    id: 'QUOTE_RECEIVED',
    label: 'Quote Received',
    icon: 'fas fa-inbox',
    color: 'amber',
    description: 'Quotes received from suppliers',
    phase: 2,
  },
  SET_MARGIN: {
    id: 'SET_MARGIN',
    label: 'Set Margin',
    icon: 'fas fa-percentage',
    color: 'blue',
    description: 'Setting profit margin on quote',
    phase: 2,
  },

  // === PHASE 3: PROFORMA & NEGOTIATION ===
  PROFORMA_GENERATED: {
    id: 'PROFORMA_GENERATED',
    label: 'Proforma Generated',
    icon: 'fas fa-file-invoice',
    color: 'teal',
    description: 'Proforma invoice generated',
    phase: 3,
  },
  PROFORMA_SENT: {
    id: 'PROFORMA_SENT',
    label: 'Proforma Sent',
    icon: 'fas fa-paper-plane',
    color: 'teal',
    description: 'Proforma invoice sent to customer',
    phase: 3,
  },
  NEGOTIATION: {
    id: 'NEGOTIATION',
    label: 'Negotiation',
    icon: 'fas fa-handshake',
    color: 'amber',
    description: 'Negotiating terms with customer',
    phase: 3,
  },
  ADJUST_RESEND: {
    id: 'ADJUST_RESEND',
    label: 'Adjust & Resend',
    icon: 'fas fa-edit',
    color: 'blue',
    description: 'Adjusting proforma for resend',
    phase: 3,
  },
  PROFORMA_RESENT: {
    id: 'PROFORMA_RESENT',
    label: 'Proforma Resent',
    icon: 'fas fa-share',
    color: 'teal',
    description: 'Updated proforma resent to customer',
    phase: 3,
  },

  // === PHASE 4: PO & PAYMENT ===
  PO_CREATED: {
    id: 'PO_CREATED',
    label: 'PO Created',
    icon: 'fas fa-clipboard-list',
    color: 'green',
    description: 'Purchase order created with supplier',
    phase: 4,
  },
  LOG_PAYMENT: {
    id: 'LOG_PAYMENT',
    label: 'Log Payment',
    icon: 'fas fa-credit-card',
    color: 'blue',
    description: 'Payment received and logged',
    phase: 4,
  },

  // === PHASE 5: PRODUCTION ===
  PRODUCTION: {
    id: 'PRODUCTION',
    label: 'Production',
    icon: 'fas fa-cogs',
    color: 'blue',
    description: 'Order in production',
    phase: 5,
  },
  PACKING_LIST_RECEIVED: {
    id: 'PACKING_LIST_RECEIVED',
    label: 'Packing List Received',
    icon: 'fas fa-box',
    color: 'green',
    description: 'Packing list received from supplier',
    phase: 5,
  },

  // === PHASE 6: DOCUMENT VERIFICATION & SHIPPING ===
  DOC_VERIFICATION: {
    id: 'DOC_VERIFICATION',
    label: 'Doc Verification',
    icon: 'fas fa-file-circle-check',
    color: 'purple',
    description: 'Verifying shipping documents',
    phase: 6,
  },
  SHIPMENT: {
    id: 'SHIPMENT',
    label: 'In Transit',
    icon: 'fas fa-ship',
    color: 'blue',
    description: 'Order in transit',
    phase: 6,
  },

  // === PHASE 7: DELIVERY ===
  DELIVERED: {
    id: 'DELIVERED',
    label: 'Delivered',
    icon: 'fas fa-check-double',
    color: 'green',
    description: 'Order delivered to customer',
    phase: 7,
  },

  // === TERMINAL STATES ===
  ORDER_REJECTED: {
    id: 'ORDER_REJECTED',
    label: 'Rejected',
    icon: 'fas fa-times-circle',
    color: 'urgent',
    description: 'Order was rejected',
    phase: 0,
  },
  ORDER_CANCELLED: {
    id: 'ORDER_CANCELLED',
    label: 'Cancelled',
    icon: 'fas fa-ban',
    color: 'urgent',
    description: 'Order was cancelled',
    phase: 0,
  },
};

/**
 * Get status configuration by ID.
 */
export function getStatusConfig(statusId: OrderStatusId): StatusBadgeConfig {
  return ORDER_STATUS_CONFIG[statusId];
}

/**
 * Get badge CSS class from color.
 */
export function getBadgeColorClass(color: BadgeColor): string {
  return `badge-${color}`;
}

/**
 * Get all statuses for a specific phase.
 */
export function getStatusesByPhase(phase: number): StatusBadgeConfig[] {
  return Object.values(ORDER_STATUS_CONFIG).filter((config) => config.phase === phase);
}

/**
 * Get all status IDs as an array.
 */
export function getAllStatusIds(): OrderStatusId[] {
  return Object.keys(ORDER_STATUS_CONFIG) as OrderStatusId[];
}

/** Terminal statuses: the order is closed and no stage action applies any more. */
export function isClosedOrderStatus(statusId: string): boolean {
  return statusId === 'ORDER_REJECTED' || statusId === 'ORDER_CANCELLED';
}
